/* Verbatim port of renderActions (service/review/index.html ~3075-3119) and
 * exportActionsCsv (~3120-3136) — §7 Action plan.
 *
 * GUARD: the original's `typeof WS !== 'undefined' && WS.available === …`
 * checks guarded against WS not existing yet (pre-am-workspace-branch
 * pages). WS is now always a real, loaded module (state/workspace.ts) by
 * the time any view renders, so those checks collapse to plain
 * `WS.available === …` — same three-way branch (true → live board,
 * null → probing skeleton, false → offline derived table), just without
 * the now-always-true `typeof` guard. `WS` itself is referenced as a bare
 * global (not imported) — same treatment as views/exec.ts and
 * views/conflicts.ts (see state/workspace.ts's `declare global` block).
 *
 * PARAMETERIZED (rule a): reads the global STATE (via actionPlan) and the
 * module-level ACT_STATUS (via actStatusStore) — takes `state:
 * AppPayloadState` as an explicit first parameter, same as every other
 * view.
 *
 * ── F3c (wave C) lit-html conversion — per the wiki.ts conventions block ──
 * The live-board (`WS.available === true`) and probing-skeleton
 * (`WS.available === null`) branches just delegate to WS.renderBoard(el)/
 * WS.renderSkeleton(el) (ws/board.ts) — untouched by this conversion, that
 * module gets its own commit next. Only this file's OWN template (the
 * offline derived read-only table) converts.
 *
 * WS.ICON is now a real `svg\`\`` TemplateResult dictionary (state/
 * workspace.ts) — `${WS.ICON.down}` (Export CSV button) is a plain lit
 * child binding (the transitional `unsafeHTML` wrapping this file shipped
 * with is gone; see the WS.ICON conversion commit).
 *
 * F4 item 3 (store decoupling): subscribes to wsChangeStore (state/
 * wsChange.ts) below so a WS data change (a task PATCH landing, refresh()
 * re-fetching the board, WS.init() finishing its probe) re-renders this
 * section — replacing the old hardcoded `renderActions(state)` call
 * state/workspace.ts's `repaintAll()`/`init()` used to make directly. This
 * ALSO covers ws/board.ts's `renderBoard`/`renderSkeleton` (see this
 * file's own `WS.available` branch below): they have no independent
 * subscription of their own — `renderActions` is their only caller, and
 * that pre-existing three-way branch (live board / probing skeleton /
 * offline derived table) is the real logic, not fan-out wiring — so
 * subscribing here is sufficient; a second subscription in ws/board.ts
 * would either be dead code or race this one for `#actionsBody`.
 */
import { html, nothing, render } from 'lit';
import { actionPlan } from '../derive/actions.ts';
import { cap, slugify } from '../lib/format.ts';
import { actStatusStore } from '../state/ui.ts';
import { payloadStore } from '../state/payload.ts';
import { wsChangeStore } from '../state/wsChange.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';

export function renderActions(state: AppPayloadState): void {
  const el = document.getElementById('actionsBody')!;
  // Server-backed shared board (the workspace layer, state/workspace.ts).
  // Offline → the derived table below renders read-only. K: the persisted
  // List|Board preference (WS._view) picks the list grouping or the kanban
  // lanes — both render into #actionsBody from the SAME ws.tasks.
  if (WS.available === true) { WS._view === 'board' ? WS.renderKanban(el) : WS.renderBoard(el); return; }
  // null = the /tasks probe is in flight (client switch or first load) —
  // a lightweight skeleton beats popping in the read-only derived table
  // just to throw it away a moment later (design-audit X4)
  if (WS.available === null) { WS.renderSkeleton(el); return; }
  const offline = WS.available === false;
  const acts = actionPlan(state, actStatusStore.get());
  const high = acts.filter((a) => a.priority === 'high').length;
  const done = acts.filter((a) => a.status === 'done').length;

  render(html`
    ${offline ? html`<div class="wsbanner"><b>Workspace service unreachable</b> — the shared board is offline, so this table is read-only (browser-local edits would split the team's state). Start the service and reload to edit.</div>` : nothing}
    <div class="finding">${acts.length} recommendations, <b>${high} high-priority</b>${done ? ` — ${done} already done` : ''}. This is the working backlog: every move from every section, in one place, with status you can track between runs.</div>
    <div class="why"><b>Why this matters:</b> a traditional audit ends with a "next steps" slide that nobody tracks. This table is the difference — priorities, effort, and live status, exportable for the client's monthly "work completed" story.</div>

    <div class="card">
      <div style="display:flex;align-items:center;gap:12px;margin-bottom:10px">
        <h3 style="font-size:15px">All recommendations</h3>
        <button class="exportbtn" style="margin-left:auto" @click=${() => exportActionsCsv(state)}>${WS.ICON.down}Export CSV</button>
      </div>
      <table class="tbl"><thead><tr><th style="width:34%">Recommendation</th><th>Category</th><th>Priority</th><th>Effort</th><th>Status</th><th>From</th></tr></thead>
      <tbody>${acts.map((a) => html`
        <tr>
          <td><b>${a.what}</b><div style="font-size:12px;color:var(--ink-2);margin-top:3px;line-height:1.5">${a.detail}</div></td>
          <td style="white-space:nowrap;font-size:12px">${a.category}</td>
          <td><span class="prio ${a.priority}">${cap(a.priority)}</span></td>
          <td style="font-size:12px">${a.effort}</td>
          <td>${offline
            // the live statuses live on the shared board; this derived table
            // has no last-known copy, so painting every row "Open" would
            // rewrite history — show the offline state instead (P0-6)
            ? html`<span style="font-size:12px;color:var(--muted)" title="statuses live on the shared board — unavailable while the workspace service is offline">— (offline)</span>`
            : html`<button class="actstatus s-${a.status === 'in-progress' ? 'progress' : a.status}" data-act="${a.id}">${a.status === 'open' ? 'Open' : a.status === 'in-progress' ? 'In progress' : 'Done'}</button>`}</td>
          <td style="font-size:11.5px;color:var(--muted);white-space:nowrap">${a.source}</td>
        </tr>`)}</tbody></table>
      <div class="caption">Read-only while the workspace service is offline.</div>
    </div>`, el);

  // no click handlers in either fallback state: browser-local writes are the
  // split-brain failure the shared board exists to kill (round-2 finding)
}

export function exportActionsCsv(state: AppPayloadState): void {
  const server = WS.available === true;
  // the two branches share the CSV columns; assignee/due exist only on the
  // server branch (the derived ActionRow has neither) — hence optional.
  const acts: Array<{ what: string; detail?: string; category?: string; priority?: string;
    effort?: string; status?: string; source?: string; assignee?: string | null; due?: string | null }> = server
    ? WS.tasks.map((t) => ({ what: t.title, detail: t.detail, category: t.category,
        priority: t.priority, effort: t.effort, status: t.status, source: t.source,
        assignee: t.assignee, due: t.due_date }))
    : actionPlan(state, actStatusStore.get());
  const q = (s: unknown) => '"' + String(s ?? '').replace(/"/g, '""') + '"';
  const head = 'Recommendation,Detail,Category,Priority,Effort,Status,Source'
    + (server ? ',Assignee,Due' : '');
  const csv = [head]
    .concat(acts.map((a) => ([a.what, a.detail, a.category, a.priority, a.effort, a.status, a.source] as unknown[])
      .concat(server ? [a.assignee, a.due] : []).map(q).join(','))).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob); const a = document.createElement('a');
  a.href = url; a.download = `${slugify(state.brand)}-action-plan.csv`; a.click(); URL.revokeObjectURL(url);
}

// F4 item 3 — see the module doc comment: re-renders §7 whenever WS's data
// changes, replacing the direct renderActions(state) call
// state/workspace.ts's repaintAll()/init() used to make.
wsChangeStore.subscribe(() => renderActions(payloadStore.get()));
